import * as React from "react";
import { cn } from "@/lib/utils";

// Big Five results chart, modelled on bigfive-test.com's "The Big Five" bar
// chart (the open-source project the client referenced). Five colour-coded
// domains, value labels, and filter pills that isolate a single domain.
//
// Our IPIP-50 scoring yields 0–100 percentiles (see services/grading/bigfive),
// so the axis runs 0–100 rather than the reference site's 0–120 (which reflects
// its 120-item variant). The visual language is otherwise the same.

export interface BigFiveScores {
  openness: number;
  conscientiousness: number;
  extraversion: number;
  agreeableness: number;
  neuroticism: number;
}

type DomainKey = keyof BigFiveScores;

interface DomainMeta {
  key: DomainKey;
  labelEn: string;
  labelJa: string;
  /** Bar fill — colours mirror bigfive-test.com. */
  bar: string;
}

// Order + colours mirror the reference chart.
const DOMAINS: DomainMeta[] = [
  { key: "neuroticism", labelEn: "Neuroticism", labelJa: "神経症的傾向", bar: "bg-[#7C5CD6]" },
  { key: "extraversion", labelEn: "Extraversion", labelJa: "外向性", bar: "bg-[#4F77E0]" },
  { key: "openness", labelEn: "Openness", labelJa: "開放性", bar: "bg-[#D6597E]" },
  { key: "agreeableness", labelEn: "Agreeableness", labelJa: "協調性", bar: "bg-[#E0A93F]" },
  { key: "conscientiousness", labelEn: "Conscientiousness", labelJa: "誠実性", bar: "bg-[#5FB87A]" },
];

const GRID = [0, 20, 40, 60, 80, 100];

function clampScore(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(100, Math.round(v)));
}

export interface BigFiveChartProps {
  scores: BigFiveScores;
  lang?: "en" | "ja";
  title?: string;
  className?: string;
}

export function BigFiveChart({ scores, lang = "en", title, className }: BigFiveChartProps) {
  const [selected, setSelected] = React.useState<DomainKey | null>(null);
  const heading = title ?? (lang === "ja" ? "ビッグファイブ" : "The Big Five");
  const allLabel = lang === "ja" ? "すべて" : "All";
  const labelFor = (d: DomainMeta) => (lang === "ja" ? d.labelJa : d.labelEn);

  return (
    <div className={cn("rounded-md border border-paper-line bg-paper-raised p-4 sm:p-5", className)}>
      <h3 className="font-serif text-xl text-ink mb-4">{heading}</h3>

      {/* Plot area: left gutter for the axis scale, then the bars. */}
      <div className="flex">
        <div className="relative w-7 shrink-0">
          {GRID.map((v) => (
            <span
              key={v}
              className="absolute right-1 -translate-y-1/2 text-[10px] tabular-nums text-ink-muted"
              style={{ bottom: `${v}%` }}
            >
              {v}
            </span>
          ))}
        </div>

        <div className="relative h-52 flex-1">
          {/* gridlines */}
          {GRID.map((v) => (
            <div
              key={v}
              aria-hidden
              className="absolute inset-x-0 border-t border-paper-line/70"
              style={{ bottom: `${v}%` }}
            />
          ))}
          {/* bars */}
          <div className="absolute inset-0 flex items-end justify-between gap-2 sm:gap-3">
            {DOMAINS.map((d) => {
              const v = clampScore(scores[d.key]);
              const active = selected === null || selected === d.key;
              const labelInside = v >= 12;
              return (
                <div key={d.key} className="flex h-full flex-1 items-end justify-center">
                  <div
                    className="relative flex w-full max-w-[56px] justify-center"
                    style={{ height: `${v}%` }}
                  >
                    <div
                      className={cn(
                        "h-full w-full rounded-t-sm transition-all duration-500",
                        d.bar,
                        active ? "opacity-100" : "opacity-20",
                      )}
                    />
                    <span
                      className={cn(
                        "absolute inset-x-0 text-center text-[11px] font-semibold tabular-nums transition-opacity",
                        labelInside ? "top-1 text-white" : "-top-4 text-ink-soft",
                        active ? "opacity-100" : "opacity-30",
                      )}
                    >
                      {v}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* x-axis labels, aligned to the bar columns (note the matching left gutter) */}
      <div className="flex">
        <div className="w-7 shrink-0" aria-hidden />
        <div className="mt-2 flex flex-1 justify-between gap-2 sm:gap-3">
          {DOMAINS.map((d) => (
            <span
              key={d.key}
              className={cn(
                "flex-1 text-center text-[10px] leading-tight sm:text-[11px]",
                selected === null || selected === d.key ? "text-ink-soft" : "text-ink-muted/60",
              )}
            >
              {labelFor(d)}
            </span>
          ))}
        </div>
      </div>

      {/* filter pills */}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5">
        <Pill active={selected === null} onClick={() => setSelected(null)}>
          {allLabel}
        </Pill>
        {DOMAINS.map((d) => (
          <Pill
            key={d.key}
            active={selected === d.key}
            onClick={() => setSelected((cur) => (cur === d.key ? null : d.key))}
          >
            {labelFor(d)}
          </Pill>
        ))}
      </div>
    </div>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full px-3 py-1 text-[11px] font-medium transition-colors",
        active
          ? "bg-accent text-paper"
          : "bg-paper-sunken text-ink-muted hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}
